import React, { Component, ErrorInfo } from 'react'
import { Box, Button, Container, Paper, Typography } from '@mui/material'
import { ErrorOutlineOutlined as ErrorOutlineIcon, Refresh as RefreshIcon, Home as HomeIcon} from '@mui/icons-material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import i18n from '../i18n'

interface Props {
  children: React.ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Global error boundary that catches unhandled React rendering errors.
 * Shows a user-friendly error page instead of a blank white screen.
 *
 * Note: Must be a class component — React error boundaries don't support hooks.
 * Uses i18n.t() directly instead of useTranslation() hook.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo)
  }

  handleReload = (): void => {
    window.location.reload()
  }

  handleGoHome = (): void => {
    window.location.href = '/dashboard'
  }

  override render(): React.ReactNode {
    if (this.state.hasError) {
      const t = i18n.t.bind(i18n)

      return (
        <Container maxWidth='sm'>
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 2,
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />

              <Typography variant='h5' gutterBottom sx={{
                fontWeight: 600
              }}>
                {t('errorBoundary.title')}
              </Typography>

              <Typography
                variant='body1'
                sx={{
                  color: 'text.secondary',
                  mb: 3
                }}>
                {t('errorBoundary.message')}
              </Typography>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Paper
                  variant='outlined'
                  sx={{
                    p: 2,
                    mb: 3,
                    textAlign: 'left',
                    bgcolor: 'grey.50',
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  <Typography variant='caption' component='pre' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                    {this.state.error.message}
                    {this.state.error.stack && `\n\n${this.state.error.stack}`}
                  </Typography>
                </Paper>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button variant='contained' startIcon={<RefreshIcon />} onClick={this.handleReload}>
                  {t('errorBoundary.reload')}
                </Button>
                <Button variant='outlined' startIcon={<HomeIcon />} onClick={this.handleGoHome}>
                  {t('errorBoundary.goHome')}
                </Button>
              </Box>
            </Paper>
          </Box>
        </Container>
      );
    }

    return this.props.children
  }
}

/**
 * Dev-only component that throws on render to test the ErrorBoundary.
 * Usage: <ErrorBoundaryTrigger /> anywhere inside <ErrorBoundary>
 * Remove after testing.
 */
const CrashComponent: React.FC = () => {
  throw new Error('Test error: ErrorBoundary trigger')
}

export const ErrorBoundaryTrigger: React.FC = () => {
  const [crash, setCrash] = React.useState(false)

  if (process.env.NODE_ENV !== 'development') return null
  if (crash) return <CrashComponent />

  return (
    <Button
      variant='outlined'
      color='error'
      size='small'
      onClick={() => setCrash(true)}
      sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999, textTransform: 'none' }}
    >
      <WarningAmberIcon sx={{ fontSize: 16, mr: 0.5 }} /> Test ErrorBoundary
    </Button>
  )
}

export default ErrorBoundary
